import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        name: 'home',
        component: () => import('@/views/Home.vue'),
      },
      {
        path: 'assets',
        name: 'assets',
        component: () => import('@/views/Assets.vue'),
      },
      {
        path: 'assets/:id',
        name: 'asset-detail',
        component: () => import('@/views/AssetDetail.vue'),
      },
      {
        path: 'trading',
        name: 'trading',
        component: () => import('@/views/Trading.vue'),
      },
      {
        path: 'trading/:pairId',
        name: 'trading-pair',
        component: () => import('@/views/TradingPair.vue'),
      },
      {
        path: 'bonding',
        name: 'bonding-list',
        component: () => import('@/views/BondingList.vue'),
      },
      {
        path: 'bonding/:assetId',
        name: 'bonding-trade',
        component: () => import('@/views/BondingTrade.vue'),
      },
      // AMM Pool Routes
      {
        path: 'pools',
        name: 'pools',
        component: () => import('@/views/PoolList.vue'),
      },
      {
        path: 'pools/:assetId',
        name: 'pool-detail',
        component: () => import('@/views/PoolDetail.vue'),
      },
      {
        path: 'pools/:assetId/liquidity',
        name: 'pool-liquidity',
        component: () => import('@/views/LiquidityManage.vue'),
      },
      {
        path: 'swap/:assetId',
        name: 'swap',
        component: () => import('@/views/SwapTrade.vue'),
      },
      // Limit Order Trading Routes
      {
        path: 'limit-trading',
        name: 'limit-trading',
        component: () => import('@/views/Trading.vue'),
      },
      {
        path: 'limit-trading/:id',
        name: 'limit-trading-pair',
        component: () => import('@/views/LimitOrderTrading.vue'),
      },
    ],
  },
  {
    path: '/auth',
    component: () => import('@/layouts/AuthLayout.vue'),
    children: [
      {
        path: 'login',
        name: 'login',
        component: () => import('@/views/auth/Login.vue'),
      },
      {
        path: 'register',
        name: 'register',
        component: () => import('@/views/auth/Register.vue'),
      },
      {
        path: 'forgot-password',
        name: 'forgot-password',
        component: () => import('@/views/auth/ForgotPassword.vue'),
      },
      {
        path: 'reset-password',
        name: 'reset-password',
        component: () => import('@/views/auth/ResetPassword.vue'),
      },
      {
        path: 'verify-email',
        name: 'verify-email',
        component: () => import('@/views/auth/VerifyEmail.vue'),
      },
    ],
  },
  {
    path: '/dashboard',
    component: () => import('@/layouts/DashboardLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/dashboard/Overview.vue'),
      },
      {
        path: 'wallet',
        name: 'wallet',
        component: () => import('@/views/dashboard/Wallet.vue'),
      },
      {
        path: 'orders',
        name: 'orders',
        component: () => import('@/views/dashboard/Orders.vue'),
      },
      {
        path: 'kyc',
        name: 'kyc',
        component: () => import('@/views/dashboard/KYC.vue'),
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/views/dashboard/Settings.vue'),
      },
      {
        path: 'security',
        name: 'security',
        component: () => import('@/views/dashboard/Security.vue'),
      },
      {
        path: 'settings/wallets',
        name: 'wallet-settings',
        component: () => import('@/views/settings/Wallets.vue'),
      },
      {
        path: 'my-assets',
        name: 'my-assets',
        component: () => import('@/views/dashboard/MyAssets.vue'),
      },
      {
        path: 'assets/apply',
        name: 'asset-apply',
        component: () => import('@/views/dashboard/AssetApplication.vue'),
      },
      {
        path: 'assets/applications',
        name: 'asset-applications',
        component: () => import('@/views/dashboard/MyApplications.vue'),
      },
      {
        path: 'assets/applications/:id',
        name: 'asset-application-detail',
        component: () => import('@/views/dashboard/AssetApplicationDetail.vue'),
      },
      {
        path: 'assets/applications/:id/edit',
        name: 'asset-application-edit',
        component: () => import('@/views/dashboard/AssetApplication.vue'),
      },
      {
        path: 'holdings',
        name: 'holdings',
        component: () => import('@/views/dashboard/Holdings.vue'),
      },
      {
        path: 'create-asset',
        name: 'create-asset',
        component: () => import('@/views/dashboard/CreateAsset.vue'),
      },
      // Revenue & Dividends Routes
      {
        path: 'revenue/submit',
        name: 'revenue-submit',
        component: () => import('@/views/dashboard/RevenueReportSubmit.vue'),
      },
      {
        path: 'revenue/reports',
        name: 'revenue-reports',
        component: () => import('@/views/dashboard/RevenueReports.vue'),
      },
      {
        path: 'revenue/reports/:id',
        name: 'revenue-report-detail',
        component: () => import('@/views/dashboard/RevenueReportDetail.vue'),
      },
      {
        path: 'dividends',
        name: 'my-dividends',
        component: () => import('@/views/dashboard/MyDividends.vue'),
      },
      {
        path: 'issuer-rebates',
        name: 'issuer-rebates',
        component: () => import('@/views/dashboard/IssuerRebates.vue'),
      },
      {
        path: 'lp-claims',
        name: 'lp-claims',
        component: () => import('@/views/dashboard/LPClaims.vue'),
      },
    ],
  },
  {
    path: '/admin',
    component: () => import('@/layouts/AdminLayout.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      {
        path: '',
        name: 'admin-dashboard',
        component: () => import('@/views/admin/Dashboard.vue'),
      },
      {
        path: 'users',
        name: 'admin-users',
        component: () => import('@/views/admin/Users.vue'),
      },
      {
        path: 'assets',
        name: 'admin-assets',
        component: () => import('@/views/admin/Assets.vue'),
      },
      {
        path: 'bonding',
        name: 'admin-bonding',
        component: () => import('@/views/admin/BondingList.vue'),
      },
      {
        path: 'bonding/:id',
        name: 'admin-bonding-detail',
        component: () => import('@/views/admin/BondingDetail.vue'),
      },
      {
        path: 'trading',
        name: 'admin-trading',
        component: () => import('@/views/admin/Trading.vue'),
      },
      {
        path: 'kyc',
        name: 'admin-kyc',
        component: () => import('@/views/admin/KYC.vue'),
      },
      {
        path: 'security',
        name: 'admin-security',
        component: () => import('@/views/admin/Security.vue'),
      },
      {
        path: 'settings',
        name: 'admin-settings',
        component: () => import('@/views/admin/Settings.vue'),
      },
      // Admin Pool Routes
      {
        path: 'pools',
        name: 'admin-pools',
        component: () => import('@/views/admin/PoolList.vue'),
      },
      {
        path: 'pools/:id',
        name: 'admin-pool-detail',
        component: () => import('@/views/admin/PoolDetail.vue'),
      },
      // Admin Trading Pairs Routes
      {
        path: 'trading-pairs',
        name: 'admin-trading-pairs',
        component: () => import('@/views/admin/TradingPairs.vue'),
      },
      // Admin Revenue & Distribution Routes
      {
        path: 'revenue/reports',
        name: 'admin-revenue-reports',
        component: () => import('@/views/admin/RevenueReports.vue'),
      },
      {
        path: 'revenue/distributions',
        name: 'admin-distributions',
        component: () => import('@/views/admin/RevenueDistributions.vue'),
      },
      {
        path: 'revenue/configs',
        name: 'admin-dividend-configs',
        component: () => import('@/views/admin/DividendConfigs.vue'),
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFound.vue'),
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return { top: 0 };
  },
});

// Navigation guard
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  // Wait for auth initialization
  if (!authStore.initialized) {
    await authStore.initialize();
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } });
    return;
  }

  // Check if route requires admin access
  if (to.meta.requiresAdmin) {
    const isAdmin = authStore.user?.role === 'admin' || authStore.user?.role === 'super_admin';
    if (!isAdmin) {
      next({ name: 'dashboard' });
      return;
    }
  }

  // Redirect authenticated users away from auth pages
  if (to.path.startsWith('/auth') && authStore.isAuthenticated) {
    next({ name: 'dashboard' });
    return;
  }

  next();
});

export default router;
